import { Switch, Typography } from '@mui/material';
import { QUERY_KEYS } from 'api/query-keys';
import { DateTime } from 'luxon';
import { accreditationsService } from 'ogp/services';
import React, { useState } from 'react';
import { useMutation, useQueryClient } from 'react-query';
import { Column } from 'react-table';
import { ContextMenuButton, LwIconButton, TableHeader } from 'redesign';
import { TFunctionNonStrict } from 'i18next';
import { IconDownload } from 'assets/img';
import { useAlert } from 'shared/components/alerts';
import { CertificateCellProps, CertificateTableRow } from '../employee-certificates-tab.types';

export const getCertificatesColumnDefinition = (
  t: TFunctionNonStrict<'web-common', undefined>,
  isLentEmployee: boolean,
  getRowActions?: (
    accreditationId: string,
    flexWorkerId: string,
    validUntil: string | null,
    endDate: string | null
  ) => React.ReactNode[]
): Column<CertificateTableRow>[] => {
  const certificateName: Column<CertificateTableRow> = {
    Header: () => (
      <TableHeader> {t('table_headers.certificate_name', { ns: 'web-common' })}</TableHeader>
    ),
    accessor: 'certificateName',
    Cell: CertificateNameCell,
  };

  const download: Column<CertificateTableRow> = {
    Header: () => <TableHeader></TableHeader>,
    accessor: 'download',
    width: 80,
    Cell: DownloadCell,
  };

  const startDate: Column<CertificateTableRow> = {
    Header: () => <TableHeader> {t('start_date', { ns: 'web-common' })}</TableHeader>,
    accessor: 'startDate',
    width: 150,
    Cell: StartDateCell,
  };

  const endDate: Column<CertificateTableRow> = {
    Header: () => <TableHeader> {t('end_date', { ns: 'web-common' })}</TableHeader>,
    accessor: 'endDate',
    width: 150,
    Cell: EndDateCell,
  };

  const trust: Column<CertificateTableRow> = {
    Header: () => <TableHeader> {t('table_headers.trusted', { ns: 'web-common' })}</TableHeader>,
    accessor: 'trust',
    width: 80,
    Cell: TrustCell,
  };

  const actions: Column<CertificateTableRow> = {
    Header: () => <TableHeader></TableHeader>,
    accessor: 'actions',
    width: 80,
    Cell: (cell: CertificateCellProps<'actions'>) => ActionsCell({ ...cell, getRowActions }),
  };

  const columns = [certificateName, download, startDate, endDate, trust, actions];
  if (isLentEmployee) {
    columns.splice(
      columns.findIndex((col) => col.accessor === 'trust'),
      1
    );
  }
  return columns;
};

const CertificateNameCell = (cell: CertificateCellProps<'certificateName'>) => {
  return (
    <Typography variant="body1" noWrap>
      {cell.value}
    </Typography>
  );
};

const DownloadCell = ({ row }: CertificateCellProps<'download'>) => {
  const { accreditationId, flexWorkerId, hasFile } = row.original;
  const { alertError } = useAlert();
  const [isLoading, setIsLoading] = useState(false);

  if (!hasFile) {
    return null;
  }

  const handleDownload = async () => {
    setIsLoading(true);
    try {
      const response = await accreditationsService.getCertificateFileUrl(
        flexWorkerId,
        accreditationId
      );
      const url = response.url;
      if (url) {
        window.open(url, '_blank');
      }
    } catch (e) {
      alertError(e);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <LwIconButton
      iconColorMode="stroke"
      onClick={handleDownload}
      disabled={isLoading}
      data-testid={`download-certificate-${accreditationId}`}
    >
      <IconDownload />
    </LwIconButton>
  );
};

const StartDateCell = (cell: CertificateCellProps<'startDate'>) => {
  if (!cell.value) {
    return (
      <Typography variant="body1" color="secondary">
        -
      </Typography>
    );
  }

  const formatted = DateTime.fromISO(cell.value).toLocaleString(DateTime.DATE_MED, {
    locale: 'nl',
  });

  return (
    <Typography variant="body1" color="secondary">
      {formatted}
    </Typography>
  );
};

const EndDateCell = (cell: CertificateCellProps<'endDate'>) => {
  const { validUntil, endDate } = cell.row.original;
  const displayDate = validUntil ?? endDate;

  if (!displayDate) {
    return (
      <Typography variant="body1" color="secondary">
        -
      </Typography>
    );
  }

  const formatted = DateTime.fromISO(displayDate).toLocaleString(DateTime.DATE_MED, {
    locale: 'nl',
  });

  return (
    <Typography variant="body1" color="secondary">
      {formatted}
    </Typography>
  );
};

const TrustCell = ({ value, row }: CertificateCellProps<'trust'>) => {
  const { accreditationId, flexWorkerId } = row.original;
  const queryClient = useQueryClient();

  const trustMutation = useMutation(
    () => accreditationsService.trustAccreditation(flexWorkerId, accreditationId),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(QUERY_KEYS.employeeCertificates(flexWorkerId));
      },
    }
  );

  const untrustMutation = useMutation(
    () => accreditationsService.untrustAccreditation(flexWorkerId, accreditationId),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(QUERY_KEYS.employeeCertificates(flexWorkerId));
      },
    }
  );

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    event.stopPropagation();
    event.preventDefault();
    if (event.target.checked) {
      trustMutation.mutate();
    } else {
      untrustMutation.mutate();
    }
  };

  return (
    <Switch
      checked={value}
      onChange={handleChange}
      size="small"
      disabled={trustMutation.isLoading || untrustMutation.isLoading}
    />
  );
};

const ActionsCell = ({
  row,
  getRowActions,
}: CertificateCellProps<'actions'> & {
  getRowActions?: (
    accreditationId: string,
    flexWorkerId: string,
    validUntil: string | null,
    endDate: string | null
  ) => React.ReactNode[];
}) => {
  const { accreditationId, flexWorkerId, validUntil, endDate } = row.original;

  if (!getRowActions) {
    return null;
  }

  const actions = getRowActions(accreditationId, flexWorkerId, validUntil, endDate);

  if (actions.length === 0) {
    return null;
  }

  return (
    <ContextMenuButton menuId={`certificate-actions-menu-${accreditationId}`}>
      {actions}
    </ContextMenuButton>
  );
};
